import { useMemo } from 'react';

import type { FeatureToggleDto } from '@/services/ws-post/src/main/resources/rest-api';
import { FeatureToggleKeyDto, listFeatureTogglesState } from '@/services/ws-post/src/main/resources/rest-api';
import type { FeatureToggleKeyDto as FeatureToggleKeyDtoCrm } from '@/services/ws-post-crm/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useRequestLegacy } from '@/js/libs/use-request-legacy';

import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';

type FeaturesMap = {
  [key in FeatureToggleKeyDto | FeatureToggleKeyDtoCrm]?: true;
};

const DEFAULT_FEATURE_TOGGLE_STATE: FeatureToggleDto[] = [];

const { useFeatureToggleContext } = createContexts({
  FeatureToggle() {
    const [featureList, { pending }] = useRequestLegacy({
      apiMethod: listFeatureTogglesState,
      defaultData: DEFAULT_FEATURE_TOGGLE_STATE,
      onFail: useRequestErrorNotification(),
    });

    const featuresMap = useMemo(
      () =>
        [...featureList].reduce<FeaturesMap>((acc, feature) => {
          if (!feature.key || !feature.value) {
            return acc;
          }

          return {
            ...acc,
            [feature.key]: true,
          };
        }, {}),
      [featureList],
    );

    const isUnderTesting = featuresMap[FeatureToggleKeyDto.TESTING];
    const isTasksEnabled = featuresMap[FeatureToggleKeyDto.TASKS];
    const isPromopostBookingEnabled = featuresMap[FeatureToggleKeyDto.PROMOPOST_BOOKING];
    const isPostInvoicingEnabled = featuresMap[FeatureToggleKeyDto.POST_INVOICING];
    const isPostCancelInvoiceEnabled = featuresMap[FeatureToggleKeyDto.POST_INVOICING_CANCELLATION];
    const isManualPostInvoicingEnabled = featuresMap[FeatureToggleKeyDto.MANUAL_POST_INVOICING];
    const isScreenRestrictionsEnabled = featuresMap[FeatureToggleKeyDto.SCREEN_RESTRICTIONS];
    const isCustomerInvoiceReferenceEnabled = featuresMap[FeatureToggleKeyDto.CUSTOMER_INVOICE_REFERENCE];
    const isAccountingNumberEnabled = featuresMap[FeatureToggleKeyDto.ACCOUNTING_NUMBER];
    const isPositionOverwritingEnabled = featuresMap[FeatureToggleKeyDto.POSITION_OVERWRITING];
    const isBriefingEmlEnabled = featuresMap[FeatureToggleKeyDto.BRIEFING_EML];
    const isDefaultServiceProductDiscountEnabled = featuresMap[FeatureToggleKeyDto.DEFAULT_SERVICE_PRODUCT_DISCOUNT];
    const isEmailSenderEnabled = featuresMap[FeatureToggleKeyDto.EMAIL_SENDER];
    const isDefaultOnlineLineItemsMaxCapacityEnabled = featuresMap[FeatureToggleKeyDto.DEFAULT_ONLINE_LINE_ITEMS_MAX_CAPACITY];
    const isReachEnabled = featuresMap[FeatureToggleKeyDto.REACH];
    const isOnlineOverbookingLimitEnabled = featuresMap[FeatureToggleKeyDto.ONLINE_POSITIONS_OVERBOOKING_VALIDATION];
    const isExistingCustomersReductionEnabled = featuresMap[FeatureToggleKeyDto.EXISTING_CUSTOMERS];
    const isPostronMobileInAppDisplaySuppressionEnabled = featuresMap[FeatureToggleKeyDto.POSTRON_MOBILE_IN_APP_DISPLAY_SUPPRESSION];
    const isPostronMobileInAppDisplayHidingEnabled = featuresMap[FeatureToggleKeyDto.POSTRON_MOBILE_IN_APP_DISPLAY_HIDING];
    const isDynamicCampaignManagementGrossEnabled = featuresMap[FeatureToggleKeyDto.DYNAMIC_CAMPAIGN_MANAGEMENT_GROSS];

    return {
      pending,
      isUnderTesting,
      isTasksEnabled,
      isPromopostBookingEnabled,
      isPostInvoicingEnabled,
      isManualPostInvoicingEnabled,
      isPostCancelInvoiceEnabled,
      isScreenRestrictionsEnabled,
      isCustomerInvoiceReferenceEnabled,
      isAccountingNumberEnabled,
      isPositionOverwritingEnabled,
      isBriefingEmlEnabled,
      isDefaultServiceProductDiscountEnabled,
      isEmailSenderEnabled,
      isDefaultOnlineLineItemsMaxCapacityEnabled,
      isReachEnabled,
      isOnlineOverbookingLimitEnabled,
      isExistingCustomersReductionEnabled,
      isMobileInAppDisabled: isPostronMobileInAppDisplaySuppressionEnabled,
      isMobileInAppDisabledView: isPostronMobileInAppDisplayHidingEnabled,
      isDynamicCampaignManagementGrossEnabled,
    };
  },
});

export { useFeatureToggleContext };
